import { Component, OnInit, signal, inject, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { NgxPaginationModule } from 'ngx-pagination';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs/operators';

import { PageSize } from 'src/app/enumeration/pageSize';
import { QrApiService } from 'src/app/service/qr/qr-api.service';
import { DateFormatPipe } from 'src/app/utils/date-format.pipe';

@Component({
  selector: 'app-qr-history',
  standalone: true,
  imports: [
    CommonModule,
    NgxPaginationModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    DateFormatPipe,
  ],
  templateUrl: './qr-history.component.html',
  styleUrl: './qr-history.component.css',
})
export class QrHistoryComponent implements OnInit {
  // ─── Modern Services Functional Injection Engine ───
  private readonly route = inject(ActivatedRoute);
  private readonly apiService = inject(QrApiService);
  private readonly destroyRef = inject(DestroyRef);

  // ─── Fine-Grained Reactive Signals Architecture ───
  readonly loading = signal<boolean>(false);
  readonly page = signal<number>(0);
  readonly pageSize = signal<number>(PageSize.QR_LIST);
  readonly allTotal = signal<number>(0);

  readonly qrcodes = signal<any[]>([]);
  readonly qrType = signal<'claim' | 'illegal'>('claim');
  readonly textalert = signal<string>('');

  // ─── Strict Component Properties ───
  loginphone: string = '';

  ngOnInit(): void {
    const routeParams = this.route.snapshot.paramMap;
    this.loginphone = String(routeParams.get('phone') || '');
    this.listQrCodes('claim');
  }

  /**
   * Browser-level clean native backward route jump tracking
   */
  goBack(): void {
    window.history.back();
  }

  /**
   * Core dynamic transaction ledger query fetch engine splitting normal vs illegal claims
   */
  listQrCodes(type: 'claim' | 'illegal'): void {
    this.qrType.set(type);
    this.loading.set(true);

    const isIllegal = type === 'illegal';
    const apiCall$ = isIllegal
      ? this.apiService.getQrCodeIllegalClaimsService(this.loginphone, this.page())
      : this.apiService.getQrCodeClaimsService(this.loginphone, this.page());

    apiCall$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loading.set(false))
      )
      .subscribe({
        next: (v) => {
          const typeLabel = isIllegal ? 'illegal' : 'normal';
          this.textalert.set(`Login phone: ${this.loginphone} Claim type: ${typeLabel}`);
          this.qrcodes.set(v?.qrCodeInfoLst || []);
          this.allTotal.set(v?.allTotal || 0);
        },
        error: (e) => {
          if (e) alert(e);
        }
      });
  }

  /**
   * Syncs interface page transitions over to the signal network tracker
   */
  handlePageChange(event: number): void {
    if (!this.loading()) {
      this.page.set(event - 1);
      this.listQrCodes(this.qrType());
    }
  }
}
